import { useMemo } from 'react';

import { useGetLatestOversightRatingsByRiskIds } from '@/hooks/queries';
import {
  isMoreRecentByTestDate,
  toTimestampOrNull,
} from '@/utils/oversightUtils';

export type OversightLeafRating = {
  Rating: number | null;
  Impact: number | null;
  Likelihood: number | null;
  ConfigId: string | null;
};

export type OversightRatingsByRisk = Map<
  string,
  { inherent?: OversightLeafRating; residual?: OversightLeafRating }
>;

const EMPTY_MAP: OversightRatingsByRisk = new Map();

type LeafWithTimestamps = {
  leaf: OversightLeafRating;
  TestDate: string | null;
  CreatedAt: string | null;
};

/**
 * Fetches the latest oversight inherent + residual rating per risk for the given
 * risk Ids, keyed by RiskId.
 *
 * "Latest" mirrors the risk page exactly
 * (getLatestOversightAssessmentRiskAssessmentResultsByRiskId): the oversight
 * result with the newest `TestDate` (tie-broken by `CreatedAtTimestamp`), both
 * read from the result row itself. Inherent (uncontrolled) and residual
 * (controlled) legs are tracked independently. There is no completion gate —
 * any oversight assessment result counts, so the rating surfaces as soon as it
 * is assigned.
 *
 * Data fetching (GraphQL / tRPC feature-flag switch) is delegated to the
 * `useGetLatestOversightRatingsByRiskIds` bridge hook; this hook owns the
 * "latest per risk leg" reduction the register consumes.
 *
 * Skips the query when oversight is disabled or no risks are visible.
 */
export const useLatestOversightRatingsByRisks = (params: {
  riskIds: string[];
  isEnabled: boolean;
}): { ratingsByRisk: OversightRatingsByRisk; loading: boolean } => {
  const { riskIds, isEnabled } = params;
  const skip = !isEnabled || riskIds.length === 0;

  const { data, loading } = useGetLatestOversightRatingsByRiskIds({
    queryArgs: { riskIds },
    shouldSkip: skip,
  });

  const ratingsByRisk = useMemo<OversightRatingsByRisk>(() => {
    if (!data) {
      return EMPTY_MAP;
    }

    const latestByRisk = new Map<
      string,
      {
        inherent?: LeafWithTimestamps;
        residual?: LeafWithTimestamps;
      }
    >();

    for (const row of data.risk_uncontrolled_oversight_result) {
      const candidate: LeafWithTimestamps = {
        leaf: {
          Rating: row.Rating ?? null,
          Impact: row.Impact ?? null,
          Likelihood: row.Likelihood ?? null,
          ConfigId: row.ConfigId ?? null,
        },
        TestDate: toTimestampOrNull(row.TestDate),
        CreatedAt: toTimestampOrNull(row.CreatedAtTimestamp),
      };
      // A finding can attach to multiple risks (insertChildRiskOversightResult
      // takes RiskIds[]). `riskParent` is server-filtered to ParentId ∈ $RiskIds,
      // so every entry maps to a risk we want to surface the rating against.
      for (const rp of row.riskParent) {
        const existing = latestByRisk.get(rp.ParentId) ?? {};
        if (
          !existing.inherent ||
          isMoreRecentByTestDate(existing.inherent, candidate)
        ) {
          existing.inherent = candidate;
        }
        latestByRisk.set(rp.ParentId, existing);
      }
    }

    for (const row of data.risk_controlled_oversight_result) {
      const candidate: LeafWithTimestamps = {
        leaf: {
          Rating: row.Rating ?? null,
          Impact: row.Impact ?? null,
          Likelihood: row.Likelihood ?? null,
          ConfigId: row.ConfigId ?? null,
        },
        TestDate: toTimestampOrNull(row.TestDate),
        CreatedAt: toTimestampOrNull(row.CreatedAtTimestamp),
      };
      for (const rp of row.riskParent) {
        const existing = latestByRisk.get(rp.ParentId) ?? {};
        if (
          !existing.residual ||
          isMoreRecentByTestDate(existing.residual, candidate)
        ) {
          existing.residual = candidate;
        }
        latestByRisk.set(rp.ParentId, existing);
      }
    }

    const result: OversightRatingsByRisk = new Map();
    for (const [id, entry] of latestByRisk) {
      result.set(id, {
        inherent: entry.inherent?.leaf,
        residual: entry.residual?.leaf,
      });
    }

    return result;
  }, [data]);

  return { ratingsByRisk, loading };
};
